/**
 * Microsoft Ads (Bing) Adapter
 *
 * Adapts event data into Microsoft Ads UET format, and into the Microsoft
 * Advertising Conversions API format for the server-side channel.
 *
 * Every adapted event carries one event_id that is pushed to the UET tag and
 * sent to the Conversions API, so Microsoft deduplicates the browser pixel and
 * the server-side event against each other.
 *
 * Documentation:
 * - https://help.ads.microsoft.com/#apex/ads/en/56684/2
 * - https://help.ads.microsoft.com/#apex/ads/en/60118/-1
 * - https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration
 *
 * @since 1.51.0
 */

(function (pmw, undefined) {

	/**
	 * The only identifiers the Microsoft Conversions API accepts. An event
	 * carrying none of them is rejected with HTTP 400 MissingUserIdentifier.
	 * clientIpAddress and clientUserAgent are NOT on the list: they improve
	 * matching but cannot identify a visitor on their own.
	 * https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration#user-data
	 */
	const MICROSOFT_IDENTIFIERS = [ 'anonymousId', 'externalId', 'em', 'ph', 'msclkid', 'idfa', 'gaid' ];

	class MicrosoftAdsAdapter {

		/**
		 * Adapt event data to Microsoft Ads format
		 *
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for Microsoft Ads or null if the event is not supported
		 */
		adapt(eventData, eventName, context) {

			const eventMap = {
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'view_item': () => this.adaptViewItem(eventData),
				'view_category': () => this.adaptViewCategory(eventData),
				'search': () => this.adaptSearch(eventData),
				'purchase': () => this.adaptPurchase(eventData),
				'everywhere_else': () => this.adaptEverywheereElse(eventData),
			};

			if (!eventMap[eventName]) {
				return null;
			}

			return this.withServerData(eventMap[eventName]());
		}

		/**
		 * Enrich the server-side (Conversions API) payload with the UET visitor
		 * ID once it exists.
		 *
		 * bat.js loads asynchronously, so on the first pageview of a session
		 * adapt() often runs before the UET tag has written _uetvid. adapt()
		 * stays best-effort and non-blocking so the browser pixel and every
		 * other pixel fire immediately; this step then waits up to ~1 second
		 * for the cookie and attaches it as anonymousId.
		 *
		 * This matters more here than for any other destination: Microsoft
		 * rejects an event outright ("MissingUserIdentifier", HTTP 400) unless
		 * userData carries at least one of anonymousId, externalId, em, ph,
		 * msclkid, idfa or gaid. clientIpAddress does NOT count. Most visitors
		 * have none of the others, so without anonymousId the event is lost.
		 *
		 * If the cookie never arrives within the cap the event is still sent
		 * (forwarding faithfully, letting Microsoft decide) and every
		 * subsequent event in the session resolves instantly.
		 *
		 * Navigation events skip this phase entirely (see the caller in
		 * event-processor.js), so the send is never deferred past unload.
		 *
		 * @param {object} pixelData - The Microsoft Ads pixel data from adapt()
		 * @returns {Promise<object>} The (possibly) enriched pixel data
		 */
		async enrichServerData(pixelData) {

			// The waiter lives in the premium bundle, alongside the rest of the
			// Conversions API helpers.
			if (typeof pmw.waitForMicrosoftVisitorId !== 'function') {
				return pixelData;
			}

			// Nothing to wait for if the visitor ID is already attached.
			if (pixelData?.user_data?.anonymousId) {
				return pixelData;
			}

			const visitorId = await pmw.waitForMicrosoftVisitorId();

			if (visitorId && pixelData?.user_data) {
				pixelData.user_data.anonymousId = visitorId;
			}

			return pixelData;
		}

		/**
		 * Whether this event can actually be delivered to the Conversions API.
		 *
		 * Microsoft requires at least one identifier from MICROSOFT_IDENTIFIERS
		 * and rejects the event outright without one. A visitor can genuinely
		 * have none: no msclkid unless they arrived from a Microsoft ad, no
		 * email unless enhanced conversions are on and they are logged in, and
		 * no anonymousId if bat.bing.com never loaded, which is what happens
		 * behind an ad blocker.
		 *
		 * Sending it anyway achieves nothing and is not free: on SweetCode
		 * Cloud the request is metered against the shop's quota before the
		 * proxy ever forwards it, and the guaranteed rejection then shows up
		 * as a delivery failure in the shop's reports. The PHP side already
		 * takes this decision on the non-SSP route
		 * (Bing_CAPI::has_supported_identifier); this applies the same rule to
		 * the browser route, which posts straight to the proxy and so never
		 * reaches that check.
		 *
		 * The browser UET tag still fires either way. Only the server-side copy
		 * is held back.
		 *
		 * @param {object} pixelData - The Microsoft Ads pixel data from adapt()
		 * @returns {boolean}
		 */
		isServerDeliverable(pixelData) {
			const userData = pixelData?.user_data || {};

			return MICROSOFT_IDENTIFIERS.some(key => Boolean(userData[key]));
		}

		/**
		 * Adapt add_to_cart event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;

			const product = eventData.product;

			return {
				event_name: 'add_to_cart',
				event_data: {
					ecomm_pagetype: 'cart',
					ecomm_prodid: product.dyn_r_ids?.[pmwDataLayer.pixels.bing.dynamic_remarketing.id_type],
				}
			};
		}

		/**
		 * Adapt view_item event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptViewItem(eventData) {
			const data = {
				event_name: '',
				event_data: {}
			};

			if (eventData?.product) {
				data.event_data.ecomm_pagetype = 'product';
				data.event_data.ecomm_prodid = eventData.product.dyn_r_ids?.[pmwDataLayer.pixels.bing.dynamic_remarketing.id_type];
			}

			return data;
		}

		/**
		 * Adapt view_category event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptViewCategory(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_pagetype: 'category',
				}
			};

			// The category of the page, as the hierarchical slug path
			// Microsoft uses for dynamic remarketing. Only the category page
			// carries one: on a product page the product ID is the signal.
			const category = pmw.getBingEcommCategory();
			if (category) data.event_data.ecomm_category = category;

			if (Object.keys(pmwDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = pmw.getBingDataLayerProducts();
			}

			return data;
		}

		/**
		 * Adapt search event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptSearch(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_query: pmw.getSearchTermFromUrl(),
					ecomm_pagetype: 'searchresults',
				}
			};

			if (Object.keys(pmwDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = pmw.getBingDataLayerProducts();
			}

			return data;
		}

		/**
		 * Adapt purchase event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 *
		 * The event ID mirrors the server-side Conversions API purchase ID
		 * ("pmw_" + order ID) so Microsoft deduplicates the UET tag and the
		 * Conversions API purchase against each other.
		 */
		adaptPurchase(eventData) {
			const data = {
				event_name: 'purchase',
				event_data: {
					ecomm_pagetype: 'purchase',
					ecomm_prodid: pmw.bing_purchase_ecomm_prodids(),
					revenue_value: pmwDataLayer.order.value.marketing,
					currency: pmwDataLayer.order.currency,
					items: pmw.bingPurchaseItems(),
				}
			};

			if (pmwDataLayer?.order?.id) {
				data.event_id = 'pmw_' + String(pmwDataLayer.order.id);
				data.transaction_id = String(pmwDataLayer.order.id);
			}

			return data;
		}

		/**
		 * Adapt everywhere_else event (generic page views)
		 * The ecomm_pagetype parameter identifies the type of page the user has visited.
		 * Valid options: home, searchresults, category, product, cart, purchase, other.
		 */
		adaptEverywheereElse(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_pagetype: pmwDataLayer?.shop?.page_type === 'front_page' ? 'home' : 'other',
				}
			};

			if (Object.keys(pmwDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = pmw.getBingDataLayerProducts();
			}

			return data;
		}

		/**
		 * Attach everything the server-side channel needs to an adapted event:
		 * the shared deduplication ID, the Conversions API custom data derived
		 * from the UET parameters, the source URL, the user identifiers and the
		 * consent signal.
		 *
		 * The UET parameters stay the single source of truth: custom_data is
		 * derived from them rather than built a second time, so the browser
		 * pixel and the Conversions API can never describe the same event
		 * differently.
		 *
		 * @param {object|null} data - The adapted event
		 * @returns {object|null}
		 */
		withServerData(data) {

			if (!data) return null;

			// One ID for both channels. Purchases already carry the order-derived ID.
			if (!data.event_id) data.event_id = pmw.getRandomEventId();

			// The UET tag reads the deduplication ID from the event parameters.
			// https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration#custom-events
			data.event_data          = data.event_data || {};
			data.event_data.event_id = data.event_id;

			data.source_url  = window.location.href;
			data.custom_data = this.toCustomData(data);

			// The user data helper lives in the premium bundle
			// (functions_premium.js). The free build ships the UET tag without
			// it; the browser event above is complete either way, and without
			// an identifier isServerDeliverable() holds the server copy back.
			data.user_data = typeof pmw.getMicrosoftAdsUserData === 'function' ? pmw.getMicrosoftAdsUserData() : {};

			// Page context the Conversions API uses for referral remarketing
			// lists and page matching. The UET tag reads both off the document
			// itself, so they are only needed on the server-side channel.
			if (document.referrer) data.referrer_url = document.referrer;
			if (document.title) data.page_title = document.title;

			// Scaffolding for toCustomData only; it lives on as customData.transactionId.
			delete data.transaction_id;

			data.ad_storage_consent = pmw.consent.categories.get().marketing ? 'G' : 'D';

			return data;
		}

		/**
		 * Translate the UET event parameters into the Conversions API
		 * customData object.
		 *
		 * https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration#payload-object-map
		 *
		 * @param {object} data - The adapted event
		 * @returns {object}
		 */
		toCustomData(data) {

			const eventData  = data.event_data || {};
			const customData = {};

			if (eventData.ecomm_pagetype) customData.pageType = eventData.ecomm_pagetype;
			if (eventData.ecomm_category) customData.ecommCategory = eventData.ecomm_category;
			if (eventData.ecomm_query) customData.searchTerm = eventData.ecomm_query;
			if (data.transaction_id) customData.transactionId = data.transaction_id;

			// itemIds is always a JSON array, while the UET parameter carries a
			// bare ID on single-product events.
			if (eventData.ecomm_prodid !== undefined && eventData.ecomm_prodid !== null) {
				const ids = (Array.isArray(eventData.ecomm_prodid) ? eventData.ecomm_prodid : [ eventData.ecomm_prodid ])
					.filter(id => id !== undefined && id !== null && id !== '')
					.map(id => String(id));

				if (ids.length) customData.itemIds = ids;
			}

			if (eventData.revenue_value !== undefined && eventData.revenue_value !== null) {
				const value = Number(eventData.revenue_value);
				if (Number.isFinite(value)) {
					// Microsoft asks for the same figure in both fields: value
					// reports the goal revenue, ecommTotalValue the cart total.
					customData.value           = value;
					customData.ecommTotalValue = value;
				}
			}

			if (eventData.currency) customData.currency = eventData.currency;
			if (Array.isArray(eventData.items) && eventData.items.length) customData.items = eventData.items;

			return customData;
		}
	}

	// Register the adapter with server-side (Conversions API) support
	pmw.registerAdapter('bing', new MicrosoftAdsAdapter(), {
		supportsS2S: true,
		supportsEventDeduplication: true,
		isActive: () => pmwDataLayer?.pixels?.bing?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured: () => !!pmwDataLayer?.pixels?.bing?.uet_tag_id,
	});

}(window.pmw = window.pmw || {}));
